import type { LayersList, PickingInfo } from "@deck.gl/core";
import { H3HexagonLayer } from "@deck.gl/geo-layers";
import { BitmapLayer } from "@deck.gl/layers";
import * as GeoTIFF from "geotiff";
import type { MjolnirGestureEvent } from "mjolnir.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import type { ResolutionType, ViewportType } from "~/lib/map/types";
import { initHexagonSelectLayer } from "~/lib/map/utils";
import { metersToLngLat } from "~/utils/geotiff";

import { useMapBase } from "./useMapBase";

type useOverlayMapProps = {
  width: number;
  height: number;
  resolution: ResolutionType;
  areaH3Indexes: string[];
  overlayImageUrls: string[];
  overlayOpacity: number;
  onHexagonClick: (h3Index: string) => void;
  dragOverlayImageMode: boolean;
  initialViewState?: Partial<ViewportType>;
};

interface OverlayImage {
  id: string;
  url: string;
  imageData: string;
  bounds: [number, number, number, number];
}

export const useOverlayMap = ({
  width,
  height,
  resolution,
  areaH3Indexes,
  overlayImageUrls,
  overlayOpacity,
  onHexagonClick,
  dragOverlayImageMode,
  initialViewState,
}: useOverlayMapProps) => {
  const { viewState, onViewStateChange, setViewState } = useMapBase({
    width,
    height,
    areaH3Indexes,
    geometry: null,
    initialViewState,
  });

  const [overlayImages, setOverlayImages] = useState<OverlayImage[]>([]);
  const [isOverlayImageDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState<[number, number] | null>(null);
  const [activeImageId, setActiveImageId] = useState<string | null>(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const lastSelectedCell = useRef<string | null>(null);
  const lastClickTime = useRef<number>(0);
  const lastClickInfo = useRef<PickingInfo | null>(null);

  // Track previous drag mode without calling setState in an effect
  const prevDragOverlayImageModeRef = useRef(dragOverlayImageMode);
  useEffect(() => {
    prevDragOverlayImageModeRef.current = dragOverlayImageMode;
  }, [dragOverlayImageMode]);

  const newOverlayImage = async (url: string): Promise<OverlayImage> => {
    const tiff = await GeoTIFF.fromUrl(url);
    const image = await tiff.getImage();
    const w = image.getWidth();
    const h = image.getHeight();

    // Get bounds in Web Mercator coordinates (in meters)
    const bounds = await image.getBoundingBox();

    // Convert Web Mercator coordinates to WGS84 (longitude/latitude).
    // The intention was to use methods like worldToLngLat from @math.gl/web-mercator, but it could not be converted properly, so it was implemented manually.
    const [minLon, minLat] = metersToLngLat(bounds[0], bounds[1]);
    const [maxLon, maxLat] = metersToLngLat(bounds[2], bounds[3]);

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Invalid tif file");
    }

    const rasterData = await image.readRasters();
    canvas.width = w;
    canvas.height = h;

    const redBand = rasterData[0] as Uint8Array;
    const greenBand = rasterData[1] as Uint8Array;
    const blueBand = rasterData[2] as Uint8Array;
    const alphaBand = rasterData[3] ? (rasterData[3] as Uint8Array) : new Uint8Array(w * h).fill(255);

    // Draw the image data onto the canvas
    const imageData = context.createImageData(w, h);
    for (let i = 0; i < w * h; i += 1) {
      imageData.data[i * 4] = redBand[i];
      imageData.data[i * 4 + 1] = greenBand[i];
      imageData.data[i * 4 + 2] = blueBand[i];
      imageData.data[i * 4 + 3] = alphaBand[i];
    }
    context.putImageData(imageData, 0, 0);

    return {
      id: `overlay-${uuidv4()}`,
      url,
      imageData: canvas.toDataURL(),
      bounds: [minLon, minLat, maxLon, maxLat],
    };
  };

  // Watch overlay images
  useEffect(() => {
    const updateOverlayImages = async () => {
      const existingUrls = new Set(overlayImages.map((data) => data.url));
      const newUrls = overlayImageUrls.filter((url) => !existingUrls.has(url));
      if (newUrls.length > 0) {
        const newImages = await Promise.all(newUrls.map(async (url) => newOverlayImage(url)));
        setOverlayImages((prev) => [...prev, ...newImages]);
      }

      setOverlayImages((prev) => prev.filter((data) => overlayImageUrls.includes(data.url)));
    };

    updateOverlayImages();
  }, [overlayImageUrls]);

  // Watch multi select mode (Shift key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setIsMultiSelectMode(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setIsMultiSelectMode(false);
        setDragStartPos(null);
        lastSelectedCell.current = null;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const updateOverlayImageBounds = useCallback((imageId: string, dx: number, dy: number) => {
    setOverlayImages((prev) =>
      prev.map((img) => {
        if (img.id !== imageId) return img;
        return {
          ...img,
          bounds: [img.bounds[0] + dx, img.bounds[1] + dy, img.bounds[2] + dx, img.bounds[3] + dy],
        };
      }),
    );
  }, []);

  // Select the next overlay image among the overlay images at the clicked coordinates
  const activateNextImage = useCallback(
    (info: PickingInfo) => {
      if (!dragOverlayImageMode) return;

      const [longitude, latitude] = info.coordinate || [0, 0];

      const clickedImages = overlayImages.filter((img) => {
        const [minLon, minLat, maxLon, maxLat] = img.bounds;
        return longitude >= minLon && longitude <= maxLon && latitude >= minLat && latitude <= maxLat;
      });
      if (clickedImages.length === 0) return;

      let nextActiveImage: OverlayImage | undefined;
      if (!activeImageId) {
        [nextActiveImage] = clickedImages;
      } else {
        const currentActiveImageIndex = clickedImages.findIndex((img) => img.id === activeImageId);

        if (currentActiveImageIndex !== -1) {
          const nextIndex = (currentActiveImageIndex + 1) % clickedImages.length;
          nextActiveImage = clickedImages[nextIndex];
        } else {
          [nextActiveImage] = clickedImages;
        }
      }

      if (nextActiveImage) {
        setActiveImageId(nextActiveImage.id);
        setIsDragging(true);
        if (info.pixel) {
          setDragStartPos([info.pixel[0], info.pixel[1]]);
        }
      }
    },
    [dragOverlayImageMode, overlayImages, activeImageId],
  );

  const startMultiSelectMode = useCallback(
    (info: PickingInfo, event: MjolnirGestureEvent) => {
      setIsMultiSelectMode(true);
      setDragStartPos([event.offsetCenter.x, event.offsetCenter.y]);
      if (info.index !== -1 && info.object && !areaH3Indexes.includes(info.object)) {
        lastSelectedCell.current = info.object;
        onHexagonClick(info.object);
      }
    },
    [areaH3Indexes, onHexagonClick],
  );

  const startDragOverlayImage = useCallback(
    (info: PickingInfo, event: MjolnirGestureEvent) => {
      const clickedLayerId = info.layer?.id;
      if (clickedLayerId) {
        setIsDragging(true);
        if (!activeImageId) {
          setActiveImageId(clickedLayerId);
        }
        setDragStartPos([event.offsetCenter.x, event.offsetCenter.y]);
      }
    },
    [activeImageId],
  );

  const isDoubleClick = useCallback((currentTime: number): boolean => {
    const timeDiff = currentTime - lastClickTime.current;
    return timeDiff < 300 && lastClickInfo.current !== null;
  }, []);

  const isInDoubleClickWindow = useCallback(
    (currentTime: number): boolean => currentTime - lastClickTime.current < 300,
    [],
  );

  const recordClick = useCallback((time: number, info: PickingInfo) => {
    lastClickTime.current = time;
    lastClickInfo.current = info;
  }, []);

  const resetClick = useCallback(() => {
    lastClickTime.current = 0;
    lastClickInfo.current = null;
  }, []);

  const handleMouseDown = useCallback(
    (info: PickingInfo, event: MjolnirGestureEvent) => {
      const now = Date.now();

      // When drag mode has just been turned ON (false -> true), clear active image.
      // This ensures a fresh state when entering drag mode, without calling setState inside an effect.
      // Note: This only clears on user interaction; if drag mode toggles without interaction,
      // the old activeImageId will persist until the first click.
      if (dragOverlayImageMode && !prevDragOverlayImageModeRef.current) {
        setActiveImageId(null);
      }

      if (isDoubleClick(now)) {
        activateNextImage(info);
        resetClick();
        return;
      }
      recordClick(now, info);
      if (isInDoubleClickWindow(now)) {
        return;
      }

      if (event.srcEvent.shiftKey) {
        startMultiSelectMode(info, event);
        return;
      }

      if (!dragOverlayImageMode) return;
      startDragOverlayImage(info, event);
    },
    [
      dragOverlayImageMode,
      isDoubleClick,
      activateNextImage,
      resetClick,
      recordClick,
      isInDoubleClickWindow,
      startMultiSelectMode,
      startDragOverlayImage,
    ],
  );

  const moveOverlayImage = useCallback(
    (point: { x: number; y: number }, dragStart: [number, number], imageId: string) => {
      const dx = (point.x - dragStart[0]) * 0.01;
      const dy = (dragStart[1] - point.y) * 0.01;

      updateOverlayImageBounds(imageId, dx, dy);
      setDragStartPos([point.x, point.y]);
    },
    [updateOverlayImageBounds],
  );

  const handleDrag = useCallback(
    (_: PickingInfo, event: MjolnirGestureEvent) => {
      const shouldMoveOverlayImage = dragStartPos && dragOverlayImageMode && isOverlayImageDragging && activeImageId;
      if (!shouldMoveOverlayImage) return;

      moveOverlayImage(event.offsetCenter, dragStartPos, activeImageId);
    },
    [dragOverlayImageMode, isOverlayImageDragging, dragStartPos, activeImageId, moveOverlayImage],
  );

  const endMultiselectMode = useCallback(() => {
    setIsMultiSelectMode(false);
    setDragStartPos(null);
    lastSelectedCell.current = null;
  }, []);

  const endDragOverlayImage = useCallback(() => {
    setIsDragging(false);
    setDragStartPos(null);
  }, []);

  const handleMouseUp = useCallback(() => {
    endMultiselectMode();
    endDragOverlayImage();
  }, [endMultiselectMode, endDragOverlayImage]);

  const selectCellUnderCursor = useCallback(
    (info: PickingInfo) => {
      if (
        info.index !== -1 &&
        info.object &&
        lastSelectedCell.current !== info.object &&
        !areaH3Indexes.includes(info.object)
      ) {
        lastSelectedCell.current = info.object;
        onHexagonClick(info.object);
      }
    },
    [onHexagonClick, areaH3Indexes],
  );

  const handleHover = useCallback(
    (info: PickingInfo) => {
      if (!isMultiSelectMode) return;
      selectCellUnderCursor(info);
    },
    [isMultiSelectMode, selectCellUnderCursor],
  );

  const bitmapLayers = overlayImages.map(
    (data) =>
      new BitmapLayer({
        id: data.id,
        image: data.imageData,
        bounds: data.bounds,
        opacity: overlayOpacity,
        pickable: dragOverlayImageMode,
        transparentColor: dragOverlayImageMode && data.id === activeImageId ? [255, 255, 255, 150] : [0, 0, 0, 0],
      }),
  );

  const layers: LayersList = [
    new H3HexagonLayer({
      id: "h3-area-view-layer",
      data: areaH3Indexes,
      pickable: true,
      wireframe: true,
      filled: true,
      extruded: true,
      elevationScale: 0,
      autoHighlight: false,
      getHexagon: (d) => d,
      getFillColor: (_d) => [239, 134, 209, 190],
    }),
    initHexagonSelectLayer(viewState, resolution, onHexagonClick),
    ...bitmapLayers,
  ];

  return {
    layers,
    viewState,
    onViewStateChange,
    setViewState,
    updateBounds: updateOverlayImageBounds,
    handleMouseDown,
    handleHover,
    handleDrag,
    handleMouseUp,
    isOverlayImageDragging,
    isMultiSelectMode,
  };
};
